import { Check } from 'lucide-react';
import type { AuthConfig } from '../../types';
import {
  PasswordStrengthWrapper,
  PasswordStrengthRequirements,
  PasswordStrengthRequirement,
} from './styled';

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

export interface AuthPasswordStrengthIndicatorProps {
  password: string;
  config: AuthConfig;
}

/**
 * Password strength indicator showing requirement checklist.
 */
export function AuthPasswordStrengthIndicator({
  password,
  config,
}: AuthPasswordStrengthIndicatorProps) {
  const requirements = createRequirements(config);

  return (
    <PasswordStrengthWrapper>
      <PasswordStrengthRequirements>
        {requirements.map((req) => (
          <PasswordStrengthRequirement key={req.label}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '1rem',
                height: '1rem',
                borderRadius: '50%',
                border: '2px solid',
                borderColor: req.test(password) ? 'transparent' : '#9ca3af',
                backgroundColor: req.test(password) ? '#059669' : 'white',
                transition: 'all 0.2s',
              }}
            >
              {req.test(password) && (
                <Check style={{ width: '0.75rem', height: '0.75rem', color: 'white' }} />
              )}
            </div>
            <span>{req.label}</span>
          </PasswordStrengthRequirement>
        ))}
      </PasswordStrengthRequirements>
    </PasswordStrengthWrapper>
  );
}

function createRequirements(config: AuthConfig): PasswordRequirement[] {
  const requirements: PasswordRequirement[] = [];

  if (config.passwordMinLength) {
    requirements.push({
      label: `At least ${config.passwordMinLength} characters`,
      test: (password) => password.length >= config.passwordMinLength,
    });
  }

  if (config.requireLowercase) {
    requirements.push({
      label: 'Contains lowercase letter',
      test: (password) => /[a-z]/.test(password),
    });
  }

  if (config.requireUppercase) {
    requirements.push({
      label: 'Contains uppercase letter',
      test: (password) => /[A-Z]/.test(password),
    });
  }

  if (config.requireNumber) {
    requirements.push({
      label: 'Contains number',
      test: (password) => /\d/.test(password),
    });
  }

  if (config.requireSpecialChar) {
    requirements.push({
      label: 'Contains special character',
      test: (password) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    });
  }

  return requirements;
}
